import { Card, CardContent, Box, Typography, Stack, Avatar, Chip } from "@mui/material";
import {
  RadioButtonUnchecked as OpenIcon,
  LocalShipping as DispatchedIcon,
  CheckCircle as ClosedIcon,
  Sensors as NodeIcon,
  Flight as HelicopterIcon,
} from "@mui/icons-material";
import type { SightingStats } from "../types";

interface SightingStatsCardsProps {
  stats: SightingStats;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: "error" | "warning" | "success" | "info";
}

const StatCard = ({ title, value, icon, color }: StatCardProps) => (
  <Card variant="outlined">
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5">{value}</Typography>
        </Box>
        <Avatar sx={{ bgcolor: `${color}.main`, color: `${color}.contrastText`, width: 36, height: 36 }}>
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

export const SightingStatsCards = ({ stats }: SightingStatsCardsProps) => {
  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 2 }}>
      {/* Source breakdown */}
      <Card variant="outlined" sx={{ minWidth: 220 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            By Source
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<NodeIcon />}
              label={`${stats.bySource.node} Node`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<HelicopterIcon />}
              label={`${stats.bySource.helicopter} Heli`}
              size="small"
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`${stats.bySource.manual + stats.bySource.third_party} Other`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Status cards */}
      <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <StatCard
            title="Open"
            value={stats.open}
            icon={<OpenIcon fontSize="small" />}
            color="error"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <StatCard
            title="Dispatched"
            value={stats.dispatched}
            icon={<DispatchedIcon fontSize="small" />}
            color="warning"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <StatCard
            title="Closed"
            value={stats.closed}
            icon={<ClosedIcon fontSize="small" />}
            color="success"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <StatCard
            title="Total"
            value={stats.total}
            icon={<NodeIcon fontSize="small" />}
            color="info"
          />
        </Box>
      </Box>
    </Stack>
  );
};
